import { useLayoutEffect, useRef, useState } from 'react';

interface Measurement {
  key: string;
  items: number[];
  more: number;
}

/**
 * How many leading items fit in one row next to a "+N" badge for the rest.
 * Items can shrink down to `minItemWidth` (never below their own width) before
 * one is dropped, and 0 means not even one fits, so only the badge is shown.
 */
export function countFittingItems(
  items: number[],
  moreWidth: number,
  containerWidth: number,
  gap: number,
  minItemWidth = Infinity,
): number {
  const widths = items.map((width) => Math.min(width, minItemWidth));
  for (let count = widths.length; count > 0; count--) {
    const shown = widths
      .slice(0, count)
      .reduce((total, width) => total + width, gap * (count - 1));
    const badge = count < widths.length ? gap + moreWidth : 0;
    if (shown + badge <= containerWidth) return count;
  }
  return 0;
}

/**
 * Fits a row of items into its container's width. While measuring
 * (`visibleCount` is null) render every item with `data-fit-item` at its
 * natural width, plus the badge with `data-fit-more`. The widths are measured
 * again whenever `itemsKey` changes, and returned as `itemWidths`.
 */
export function useFittingItemCount(
  itemsKey: string,
  gap: number,
  minItemWidth?: number,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [measurement, setMeasurement] = useState<Measurement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const measured = measurement?.key === itemsKey ? measurement : null;

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (measured || !container) return;
    const widthOf = (element: Element) => element.getBoundingClientRect().width;
    setMeasurement({
      key: itemsKey,
      items: Array.from(container.querySelectorAll('[data-fit-item]'), widthOf),
      more: Array.from(container.querySelectorAll('[data-fit-more]'), widthOf)
        .concat(0)
        .reduce((a, b) => Math.max(a, b)),
    });
  }, [itemsKey, measured]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const update = () => setContainerWidth(container.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const visibleCount =
    measured && containerWidth !== null
      ? countFittingItems(
          measured.items,
          measured.more,
          containerWidth,
          gap,
          minItemWidth,
        )
      : null;

  return { containerRef, visibleCount, itemWidths: measured?.items ?? null };
}
